import { 
    collection, 
    doc, 
    getDoc, 
    getDocs, 
    query, 
    where, 
    onSnapshot
} from 'firebase/firestore';
import { db } from '@/firebase-config';
import type { FileNode } from '../types';
import { buildProjectSubcollectionPath, type ProjectScope } from '../../sync/project-scope';
import { getEffectiveUserId } from '@/features/auth/current-user-identity';
import { sanitizeDocumentForFirestore } from '@/utils/firestore-sanitizer';
import { enqueueStructuredEntityWrite, type StructuredEntityWriteRegistration } from '../../sync/structured-entity-write';

const FILES_COLLECTION = 'files';
const FILE_CREATE_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'files',
    capability: 'CREATE_FILES',
    conflictPrefix: FILES_COLLECTION,
    allowedKinds: ['patchEntity'],
    authErrorMessage: 'Authentication required to preserve file project writes',
};
const FILE_EDIT_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'files',
    capability: 'EDIT_FILES',
    conflictPrefix: FILES_COLLECTION,
    allowedKinds: ['patchEntity', 'deleteEntity'],
    authErrorMessage: 'Authentication required to preserve file project writes',
};

type EnqueueFileOperationInput = {
    scope: ProjectScope;
    fileId: string;
    kind: 'patchEntity' | 'deleteEntity';
    payload: Record<string, unknown>;
    actorId?: string;
    registration?: StructuredEntityWriteRegistration;
};

type FileSnapshotData = {
    id: string;
    data: () => Record<string, unknown>;
};

function requireFilesActorId(): string {
    const actorId = getEffectiveUserId();
    if (!actorId) {
        throw new Error('Authentication required to preserve file project writes');
    }
    return actorId;
}

function stripPermanentAccessUrls(file: FileNode): FileNode {
    const {
        url: _url,
        downloadUrl: _downloadUrl,
        ...sanitized
    } = file as FileNode & { downloadUrl?: unknown };
    const storageRef = typeof sanitized.storageRef === 'string' && sanitized.storageRef.trim()
        ? sanitized.storageRef
        : undefined;
    const storagePath = typeof sanitized.storagePath === 'string' && sanitized.storagePath.trim()
        ? sanitized.storagePath
        : undefined;

    return {
        ...sanitized,
        ...(storageRef ? { storageRef } : storagePath ? { storageRef: storagePath } : {}),
    } as FileNode;
}

function fileNodeFromSnapshot(snapshot: FileSnapshotData): FileNode {
    return stripPermanentAccessUrls({ id: snapshot.id, ...snapshot.data() } as FileNode);
}

async function enqueueFileOperation(input: EnqueueFileOperationInput): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope: input.scope,
        registration: input.registration ?? FILE_EDIT_WRITE_REGISTRATION,
        entityId: input.fileId,
        actorId: input.actorId ?? requireFilesActorId(),
        kind: input.kind,
        payload: sanitizeDocumentForFirestore({
            ...input.payload,
            id: input.fileId,
        }),
    });
}

async function enqueueFileMetadataPatch(input: Omit<EnqueueFileOperationInput, 'kind'>): Promise<void> {
    await enqueueFileOperation({
        ...input,
        kind: 'patchEntity',
    });
}

/**
 * Queues a new file metadata node for project write preservation.
 */
export async function createFileNode(
    scope: ProjectScope,
    parentId: string,
    name: string,
    type: 'file' | 'folder',
    metadata?: {
        mimeType?: string;
        size?: number;
        url?: string;
        thumbnailUrl?: string;
        storagePath?: string;
        visibility?: 'all' | 'admin-only' | 'owner-only';
    },
    providedFileId?: string // Optional: use this ID instead of generating a new one
): Promise<string> {
    const userId = requireFilesActorId();

    const fileId = providedFileId || crypto.randomUUID();
    const timestamp = Date.now();
    const { visibility: _visibility, ...sanitizedMetadata } = Object.fromEntries(
        Object.entries(metadata ?? {}).filter(([, value]) => value !== undefined)
    ) as Record<string, unknown>;

    const fileNode: Omit<FileNode, 'id'> = {
        projectId: scope.projectId,
        parentId,
        name,
        type,
        createdAt: timestamp,
        createdBy: userId,
        ownerUserId: userId,
        updatedAt: timestamp,
        versionNumber: 1,
        visibility: 'owner-only',
        policy: {
            version: 1,
            ownerUserId: userId,
            grants: [],
            defaultDeny: true,
        },
        readOnly: false,
        deleted: false,
        ...sanitizedMetadata
    };

    await enqueueFileMetadataPatch({
        scope,
        fileId,
        actorId: userId,
        registration: FILE_CREATE_WRITE_REGISTRATION,
        payload: { ...fileNode },
    });

    return fileId;
}

/**
 * Queues file metadata updates for project write preservation.
 */
export async function updateFileNode(
    scope: ProjectScope,
    fileId: string,
    updates: Partial<FileNode>
): Promise<void> {
    await enqueueFileMetadataPatch({
        scope,
        fileId,
        payload: {
            ...updates,
            updatedAt: Date.now()
        },
    });
}

/**
 * Gets a single file node
 */
export async function getFileNode(scope: ProjectScope, fileId: string): Promise<FileNode | null> {
    const fileRef = doc(db, buildProjectSubcollectionPath(scope, FILES_COLLECTION), fileId);
    const snap = await getDoc(fileRef);
    
    if (!snap.exists()) return null;
    
    return fileNodeFromSnapshot(snap);
}

/**
 * Lists all non-deleted files in a folder
 */
export async function listFiles(
    scope: ProjectScope,
    parentId: string = 'root'
): Promise<FileNode[]> {
    const filesRef = collection(db, buildProjectSubcollectionPath(scope, FILES_COLLECTION));
    const q = query(
        filesRef,
        where('parentId', '==', parentId),
        where('deleted', '==', false)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(fileNodeFromSnapshot);
}

/**
 * Lists all deleted files (Trash)
 */
export async function listDeletedFiles(scope: ProjectScope): Promise<FileNode[]> {
    const filesRef = collection(db, buildProjectSubcollectionPath(scope, FILES_COLLECTION));
    const q = query(filesRef, where('deleted', '==', true));

    const snapshot = await getDocs(q);
    return snapshot.docs.map(fileNodeFromSnapshot);
}

/**
 * Soft-deletes a file (moves to trash)
 */
export async function moveToTrash(scope: ProjectScope, fileId: string): Promise<void> {
    const timestamp = Date.now();
    await enqueueFileMetadataPatch({
        scope,
        fileId,
        payload: {
            deleted: true,
            deletedAt: timestamp,
            updatedAt: timestamp,
        },
    });
}

/**
 * Restores a file from trash
 */
export async function restoreFromTrash(scope: ProjectScope, fileId: string): Promise<void> {
    await enqueueFileMetadataPatch({
        scope,
        fileId,
        payload: {
            deleted: false,
            deletedAt: null,
            updatedAt: Date.now(),
        },
    });
}

/**
 * Queues a permanent file delete for server-side review.
 */
export async function permanentlyDeleteFile(scope: ProjectScope, fileId: string): Promise<void> {
    await enqueueFileOperation({
        scope,
        fileId,
        kind: 'deleteEntity',
        payload: { id: fileId },
    });
}

/**
 * Renames a file or folder
 */
export async function renameFileNode(
    scope: ProjectScope,
    fileId: string,
    newName: string
): Promise<void> {
    await enqueueFileMetadataPatch({
        scope,
        fileId,
        payload: {
            name: newName,
            updatedAt: Date.now(),
        },
    });
}

/**
 * Searches files by name
 */
export async function searchFiles(
    scope: ProjectScope,
    searchTerm: string
): Promise<FileNode[]> {
    const filesRef = collection(db, buildProjectSubcollectionPath(scope, FILES_COLLECTION));
    const q = query(filesRef, where('deleted', '==', false));

    const snapshot = await getDocs(q);
    const allFiles = snapshot.docs.map(fileNodeFromSnapshot);

    // Client-side filtering (Firestore doesn't support case-insensitive LIKE)
    return allFiles.filter(file => 
        file.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
}

/**
 * Subscribe to real-time file list updates
 */
export function subscribeToFiles(
    scope: ProjectScope,
    parentId: string,
    onUpdate: (files: FileNode[]) => void
): () => void {
    const filesRef = collection(db, buildProjectSubcollectionPath(scope, FILES_COLLECTION));
    const q = query(
        filesRef,
        where('parentId', '==', parentId),
        where('deleted', '==', false)
    );

    return onSnapshot(q, (snapshot) => {
        const files = snapshot.docs.map(fileNodeFromSnapshot);
        onUpdate(files);
    });
}

/**
 * Lists all non-deleted files owned by a specific user
 */
export async function listPersonalFiles(
    scope: ProjectScope,
    userId: string
): Promise<FileNode[]> {
    const filesRef = collection(db, buildProjectSubcollectionPath(scope, FILES_COLLECTION));
    const q = query(
        filesRef,
        where('createdBy', '==', userId),
        where('deleted', '==', false)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(fileNodeFromSnapshot);
}














